import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from './moment'
import { classNames, Icon, iconProps } from '@zambezi/sdk/common'

/**
 * Create a React Date Stepper component
 * @param {import('./interfaces').DateStepperProps} props
 * @returns {JSX.Element}
 */
function DateStepper (props) {
  const {
    date,
    prevDisabled,
    nextDisabled,
    renderLabel,
    stepValue,
    stepUnit: stepUnitProp,
    className,
    iconProps
  } = props
  const stepUnit = stepUnitProp.slice(0, -1)
  const previousButtonTitle = useMemo(() => {
    if (stepValue > 1) return `Go to previous ${stepValue} ${stepUnit}s`
    return `Go to previous ${stepUnit}`
  }, [stepUnit, stepUnit])
  const nextButtonTitle = useMemo(() => {
    if (stepValue > 1) return `Go to next ${stepValue} ${stepUnit}s`
    return `Go to next ${stepUnit}`
  }, [stepUnit, stepUnit])

  return (
    <div className={classNames('zb-date-stepper', className)}>
      <button
        type='button'
        className={classNames(
          'zb-date-stepper-button zb-date-stepper-prev-button',
          {
            'zb-date-stepper-button-is-disabled': prevDisabled
          }
        )}
        disabled={prevDisabled}
        onClick={handlePrevClick}
        aria-label={previousButtonTitle}
      >
        <Icon
          name='core-date-picker-prevyear'
          title={previousButtonTitle}
          iconProps={iconProps}
        />
      </button>

      <span className='zb-date-stepper-label'>
        {date ? renderLabel(date) : null}
      </span>

      <button
        type='button'
        className={classNames(
          'zb-date-stepper-button zb-date-stepper-next-button',
          {
            'zb-date-stepper-button-is-disabled': nextDisabled
          }
        )}
        disabled={nextDisabled}
        onClick={handleNextClick}
        aria-label={nextButtonTitle}
      >
        <Icon
          name='core-date-picker-nextyear'
          title={nextButtonTitle}
          iconProps={iconProps}
        />
      </button>
    </div>
  )

  function handlePrevClick (e) {
    const newDate = moment(date).subtract(stepValue, stepUnit).toDate()

    props.onPrevDate(newDate)
  }

  function handleNextClick (e) {
    const newDate = moment(date).add(stepValue, stepUnit).toDate()

    props.onNextDate(newDate)
  }
}

DateStepper.propTypes = {
  date: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment),
    PropTypes.number
  ]),
  nextDisabled: PropTypes.bool,
  prevDisabled: PropTypes.bool,
  stepValue: PropTypes.number,
  stepUnit: PropTypes.oneOf(['days', 'months', 'years']),
  renderLabel: PropTypes.func,
  onNextDate: PropTypes.func,
  onPrevDate: PropTypes.func,
  className: PropTypes.string,
  iconProps
}

DateStepper.defaultProps = {
  nextDisabled: false,
  prevDisabled: false,
  stepValue: 1,
  stepUnit: 'years',
  renderLabel: String,
  onNextDate: () => {},
  onPrevDate: () => {}
}

export default DateStepper
